import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import Joi from "joi";
import Input from "./common/input";
import Select from "./common/select";
import Checkbox from "./common/checkbox";
import http from "../services/http";
import UserContext from "../context/userContext";

const schema = {
  ITEM_CODE: Joi.string().required(),
  MEASUREMENT_CODE: Joi.string().required(),
  STATISTIC_CODE: Joi.string().required(),
  UNIT_NO: Joi.string().required(),
  PERIOD_CODE: Joi.string().required(),
  HEADING1: Joi.string().required(),
  HEADING2: Joi.string().allow("").required(),
  HEADING3: Joi.string().allow("").required(),
  IN_USE_FLAG: Joi.boolean().required(),
  AUDIT_FLAG: Joi.boolean().required(),
  PTYP_CODE: Joi.string().required(),
  SFNC_CODE: Joi.string().allow("").required(),
  PROCESS_TAG: Joi.string().allow("").required(),
  PCAT_CODE: Joi.string().required().label("Parameter Category"),
  DISPLAY_PRECISION: Joi.number().min(0).max(5).required().label("Precision"),
  CONSTANT_VALUE: Joi.number().allow("").required(),
  METER_ROLLOVER_VALUE: Joi.number().allow("").required(),
  UPPER_ABS_LIMIT: Joi.number().allow("").required(),
  UPPER_CHK_LIMIT: Joi.number().allow("").required(),
  LOWER_CHK_LIMIT: Joi.number().allow("").required(),
  LOWER_ABS_LIMIT: Joi.number().allow("").required(),
  COMMENTS: Joi.string().allow(""),
  UNITS: Joi.string().allow(""),
  LABEL: Joi.string().allow(""),
  CCF_CODE: Joi.string().allow("").label("CCF Analysis")
};

const ParameterForm = () => {
  const [user] = useContext(UserContext);

  const [standardFunctions, setStandardFunctions] = useState([]);
  const [parameterTypes, setParameterTypes] = useState([]);
  const [parameterGroups, setParameterGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [ccfAnalysis, setCCFAnalysis] = useState([]);
  const [errors, setErrors] = useState({});
  const [checked, setChecked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [lastCreated, setLastCreated] = useState("");
  const [parameter, setParameter] = useState({
    ITEM_CODE: "",
    MEASUREMENT_CODE: "",
    STATISTIC_CODE: "",
    UNIT_NO: "",
    PERIOD_CODE: "",
    HEADING1: "",
    HEADING2: "",
    HEADING3: "",
    COMMENTS: "",
    UNITS: "",
    LABEL: "",
    PTYP_CODE: "REA",
    IN_USE_FLAG: true,
    AUDIT_FLAG: false,
    SFNC_CODE: "",
    PROCESS_TAG: "",
    PCAT_CODE: "",
    DISPLAY_PRECISION: 3,
    CONSTANT_VALUE: "",
    METER_ROLLOVER_VALUE: "",
    UPPER_ABS_LIMIT: "",
    UPPER_CHK_LIMIT: "",
    LOWER_CHK_LIMIT: "",
    LOWER_ABS_LIMIT: "",
    CCF_CODE: ""
  });
  
  useEffect(() => {
    document.title = "FTR - New Parameter";
    return () => {
      document.title = "FTR";
    };
  });

  useEffect(() => {
    async function getFunctions() {
      try {
        const { data } = await http.get("/standard-functions");
        setStandardFunctions([{ CODE: "" }, ...data]);
      }
      catch {
        //ignore error
      }
    }

    async function getTypes() {
      try {
        const { data } = await http.get("/parameter-types");
        setParameterTypes(data);
      }
      catch {
        //ignore error
      }
    }

    async function getGroups() {
      try {
        const { data } = await http.get("/parameter-groups");
        setParameterGroups([{ CODE: "" }, ...data]);
      }
      catch {
        //ignore error
      }
    }

    async function getItems() {
      try {
        const { data } = await http.get("/lookups/item");
        setItems(data);
      }
      catch {
        //ignore error
      }
    }

    async function getMeasurements() {
      try {
        const { data } = await http.get("/lookups/measurement");
        setMeasurements(data);
      }
      catch {
        //ignore error
      }
    }

    async function getStatistics() {
      try {
        const { data } = await http.get("/lookups/statistic");
        setStatistics(data);
      }
      catch {
        //ignore error
      }
    }

    async function getPeriods() {
      try {
        const { data } = await http.get("/lookups/period");
        setPeriods(data);
      }
      catch {
        //ignore error
      }
    }

    async function getCCFAnalysis() {
      try {
        const { data } = await http.get("/ccf-analysis");
        setCCFAnalysis([{ CODE: "" }, ...data]);
      }
      catch {
        //ignore error
      }
    }

    getFunctions();
    getTypes();
    getGroups();
    getItems();
    getMeasurements();
    getStatistics();
    getPeriods();
    getCCFAnalysis();
  }, []);

  useEffect(() => {
    async function getParameter(code) {
      try {
        await http.get(`/parameter/${code}`);
        toast.error("Parameter Already Exists");
        setChecked(false);
      } catch (ex) {
        setChecked(true);
      }
    }
    if (
      parameter.ITEM_CODE !== "" &&
      parameter.MEASUREMENT_CODE !== "" &&
      parameter.STATISTIC_CODE !== "" &&
      parameter.UNIT_NO !== "" &&
      parameter.PERIOD_CODE !== "" &&
      checked === false
    ) {
      const codeEnd = [parameter.UNIT_NO, parameter.PERIOD_CODE].join("");
      const code = [
        parameter.ITEM_CODE,
        parameter.MEASUREMENT_CODE,
        parameter.STATISTIC_CODE,
        codeEnd,
      ].join("-");
      if (lastCreated === code) return;
      getParameter(code);
    }
  }, [parameter, user, checked, lastCreated]);

  function handleChange({ currentTarget: input }) {
    const newErrors = { ...errors };
    const errorMessage = validateProperty(input, schema);
    if (errorMessage) newErrors[input.name] = errorMessage;
    else delete newErrors[input.name];
    setErrors(newErrors);

    const newParameter = { ...parameter };
    const value = input.type === "checkbox" ? input.checked : input.value;
    newParameter[input.name] = value;

    const parameterHeadings = setHeadings(input, newParameter);
    if (
      input.name === "ITEM_CODE" ||
      input.name === "MEASUREMENT_CODE" ||
      input.name === "STATISTIC_CODE" ||
      input.name === "UNIT_NO" ||
      input.name === "PERIOD_CODE"
    ) {
      setChecked(false);
    }

    if (input.name === "PTYP_CODE" && input.value !== "SIM")
      parameterHeadings.SFNC_CODE = "";
    if (input.name === "PTYP_CODE" && input.value !== "CON")
      parameterHeadings.CONSTANT_VALUE = "";
    if (input.name === "PERIOD_CODE" && input.value === "C")
      parameterHeadings.PTYP_CODE = "CON";
    if (
      input.name === "PERIOD_CODE" &&
      input.value !== "C" &&
      parameterHeadings.PTYP_CODE === "CON"
    )
      parameterHeadings.PTYP_CODE = "REA";
    setParameter(parameterHeadings);
  }

  function validateProperty({ name, value }, schema) {
    const obj = { [name]: value };
    const propSchema = Joi.object({ [name]: schema[name] });
    const { error } = propSchema.validate(obj);
    return error ? error.details[0].message : null;
  }

  function validate(body, schema) {
    const options = { abortEarly: false };
    const { error } = Joi.object(schema).validate(body, options);
    if (!error) return null;

    const errors = {};
    for (let item of error.details) errors[item.path[0]] = item.message;

    return errors;
  }

  function setHeadings(input, newParameter) {
    if (input.name === "ITEM_CODE") {
      const itemIndex = items.findIndex(
        (x) => x.code === newParameter.ITEM_CODE
      );
      if (itemIndex !== -1)
        newParameter.HEADING1 = items[itemIndex].description;
    }
    if (input.name === "MEASUREMENT_CODE") {
      const mIndex = measurements.findIndex(
        (x) => x.code === newParameter.MEASUREMENT_CODE
      );
      if (mIndex !== -1)
        newParameter.HEADING2 = measurements[mIndex].description;
    }
    if (input.name === "STATISTIC_CODE" || input.name === "PERIOD_CODE") {
      const sIndex = statistics.findIndex(
        (x) => x.code === newParameter.STATISTIC_CODE
      );
      const periodIndex = periods.findIndex(
        (x) => x.code === newParameter.PERIOD_CODE
      );
      if (sIndex !== -1) newParameter.HEADING3 = statistics[sIndex].description;
      if (periodIndex !== -1)
        newParameter.HEADING3 += ` ${periods[periodIndex].description}`;
    }
    return newParameter;
  }

  async function handleCreate(e) {
    e.preventDefault();

    const newErrors = validate(parameter, schema);
    setErrors(newErrors || {});
    if (newErrors !== null) return;
    if (creating) return;

    try {
      setCreating(true);
      const { data } = await http.post(`/parameter`, parameter);

      setLastCreated(data.CODE);
      setChecked(false);
      
      // toast.success(`Parameter ${data.CODE} Created`, {
      //   onClose: () => window.location ='/parameters'
      // });
      toast.success(`Parameter ${data.CODE} Created`);
    } catch (ex) {
      if (ex.response) {
        toast.error(ex.response.data);
      }
    }
    setCreating(false);
  }

  return (
    <div className="col mt-4">
      <div className="container">
        <form>
          <h3 className="display-4 text-center text-success">{`${parameter.ITEM_CODE}-${parameter.MEASUREMENT_CODE}-${parameter.STATISTIC_CODE}-${parameter.UNIT_NO}${parameter.PERIOD_CODE}`}</h3>
          <div className="form-row">
            <div className="col">
              <Select
                name="ITEM_CODE"
                label="Item*"
                placeholder="Item"
                value={parameter.ITEM_CODE}
                options={items}
                onChange={handleChange}
                error={errors.ITEM_CODE}
                listKey="code"
                listName="name"
              />
            </div>
            <div className="col">
              <Select
                name="MEASUREMENT_CODE"
                label="Measurement*"
                placeholder="Measurement"
                value={parameter.MEASUREMENT_CODE}
                options={measurements}
                onChange={handleChange}
                error={errors.MEASUREMENT_CODE}
                listKey="code"
                listName="name"
              />
            </div>
            <div className="col">
              <Select
                name="STATISTIC_CODE"
                label="Statistic*"
                placeholder="Statistic"
                value={parameter.STATISTIC_CODE}
                options={statistics}
                onChange={handleChange}
                error={errors.STATISTIC_CODE}
                listKey="code"
                listName="name"
              />
            </div>
            <div className="col">
              <Input
                name="UNIT_NO"
                label="Unit Number*"
                placeholder="Value"
                value={parameter.UNIT_NO}
                onChange={handleChange}
                error={errors.UNIT_NO}
              />
            </div>
            <div className="col">
              <Select
                name="PERIOD_CODE"
                label="Period*"
                placeholder="Period"
                value={parameter.PERIOD_CODE}
                options={periods}
                onChange={handleChange}
                error={errors.PERIOD_CODE}
                listKey="code"
                listName="description"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="col-6">
              <Input
                name="HEADING1"
                label="Heading 1*"
                placeholder="Heading"
                value={parameter.HEADING1}
                onChange={handleChange}
                error={errors.HEADING1}
              />
              <Input
                name="HEADING2"
                label="Heading 2"
                placeholder="Heading"
                value={parameter.HEADING2}
                onChange={handleChange}
                error={errors.HEADING2}
              />
              <Input
                name="HEADING3"
                label="Heading 3"
                placeholder="Heading"
                value={parameter.HEADING3}
                onChange={handleChange}
                error={errors.HEADING3}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="UNITS"
                    label="Units"
                    placeholder="e.g. kg, ppm"
                    value={parameter.UNITS}
                    onChange={handleChange}
                    error={errors.UNITS}
                  />
                </div>
                <div className="col">
                  <Input
                    name="LABEL"
                    label="Short Label"
                    placeholder="Label"
                    value={parameter.LABEL}
                    onChange={handleChange}
                    error={errors.LABEL}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="UPPER_ABS_LIMIT"
                    label="Upper Absolute Limit"
                    placeholder="Limit"
                    value={parameter.UPPER_ABS_LIMIT}
                    onChange={handleChange}
                    error={errors.UPPER_ABS_LIMIT}
                  />
                </div>
                <div className="col">
                  <Input
                    name="UPPER_CHK_LIMIT"
                    label="Upper Check Limit"
                    placeholder="Limit"
                    value={parameter.UPPER_CHK_LIMIT}
                    onChange={handleChange}
                    error={errors.UPPER_CHK_LIMIT}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="LOWER_ABS_LIMIT"
                    label="Lower Absolute Limit"
                    placeholder="Limit"
                    value={parameter.LOWER_ABS_LIMIT}
                    onChange={handleChange}
                    error={errors.LOWER_ABS_LIMIT}
                  />
                </div>
                <div className="col">
                  <Input
                    name="LOWER_CHK_LIMIT"
                    label="Lower Check Limit"
                    placeholder="Limit"
                    value={parameter.LOWER_CHK_LIMIT}
                    onChange={handleChange}
                    error={errors.LOWER_CHK_LIMIT}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Checkbox
                    name="IN_USE_FLAG"
                    label="In Use"
                    checked={parameter.IN_USE_FLAG}
                    onChange={handleChange}
                  />
                </div>
                <div className="col">
                  <Checkbox
                    name="AUDIT_FLAG"
                    label="Audit"
                    checked={parameter.AUDIT_FLAG}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="row">
                <div className="col">
                  <Select
                    name="PTYP_CODE"
                    label="Parameter Type*"
                    placeholder="Type"
                    value={parameter.PTYP_CODE}
                    options={parameterTypes}
                    onChange={handleChange}
                    listKey="CODE"
                    listName="DESCRIPTION"
                    error={errors.PTYP_CODE}
                  />
                </div>
                <div className="col">
                  <Select
                    name="SFNC_CODE"
                    label="Calculation"
                    placeholder="Function"
                    value={parameter.SFNC_CODE}
                    options={standardFunctions}
                    onChange={handleChange}
                    listKey="CODE"
                    listName="DESCRIPTION"
                    error={errors.SFNC_CODE}
                    disabled={parameter.PTYP_CODE !== "SIM"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="PROCESS_TAG"
                    label="Process Tag"
                    placeholder="Tagname"
                    value={parameter.PROCESS_TAG}
                    error={errors.PROCESS_TAG}
                    onChange={handleChange}
                    disabled={parameter.PTYP_CODE !== "REA"}
                  />
                </div>
                <div className="col">
                  <Select
                    name="PCAT_CODE"
                    label="Parameter Category*"
                    placeholder="Category"
                    value={parameter.PCAT_CODE}
                    options={parameterGroups}
                    onChange={handleChange}
                    error={errors.PCAT_CODE}
                    listKey="CODE"
                    listName="DESCRIPTION"
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="DISPLAY_PRECISION"
                    label="Decimal Places*"
                    placeholder="Precision"
                    value={parameter.DISPLAY_PRECISION}
                    error={errors.DISPLAY_PRECISION}
                    onChange={handleChange}
                  />
                </div>
                <div className="col">
                  <Input
                    name="CONSTANT_VALUE"
                    label="Constant Value"
                    placeholder="Number"
                    value={parameter.CONSTANT_VALUE}
                    error={errors.CONSTANT_VALUE}
                    onChange={handleChange}
                    disabled={parameter.PTYP_CODE !== "CON"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="METER_ROLLOVER_VALUE"
                    label="Meter Rollover"
                    placeholder="Value"
                    value={parameter.METER_ROLLOVER_VALUE}
                    onChange={handleChange}
                    error={errors.METER_ROLLOVER_VALUE}
                    disabled={parameter.PTYP_CODE !== "REA"}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <Input
                    name="COMMENTS"
                    label="Comments"
                    placeholder="Comment"
                    value={parameter.COMMENTS}
                    onChange={handleChange}
                    error={errors.COMMENTS}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                <Select
                    name="CCF_CODE"
                    label="CCF Analysis"
                    placeholder="CCF Analysis"
                    value={parameter.CCF_ANALYSIS}
                    options={ccfAnalysis}
                    onChange={handleChange}
                    error={errors.CCF_ANALYSIS}
                    listKey="code"
                    listName="name"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-row mt-3">
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={!checked || creating}
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ParameterForm;
